import { tokenize, TokenType, type Token } from './tokenizer';
import type { QuerySchema, FilterOperator } from './schema';

export interface ActiveFilter {
  id: string;
  key: string;
  operator: FilterOperator;
  value: string;
  values?: string[];
  highValue?: string;
  raw?: string;
}

export interface ParsedQueryState {
  bareText: string;
  filters: ActiveFilter[];
  rawRemainder?: string;
}

let filterIdCounter = 0;
export function generateFilterId(key: string): string {
  return `${key}_${Date.now()}_${++filterIdCounter}`;
}

const RANGE_PATTERN = /^([<>]=?)?(-?\d+(?:\.\d+)?)(?:\.\.(-?\d+(?:\.\d+)?))?$/;

/** Parses operator and values from a raw filter value string. */
export function parseOperatorAndValue(rawVal: string): {
  operator: FilterOperator;
  value: string;
  highValue?: string;
} {
  const trimmed = rawVal.trim();

  // Check for between range e.g. "15..25"
  const rangeMatch = trimmed.match(RANGE_PATTERN);
  if (rangeMatch) {
    const op = rangeMatch[1];
    const num1 = rangeMatch[2];
    const num2 = rangeMatch[3];

    if (num2 !== undefined) {
      return { operator: 'between', value: num1, highValue: num2 };
    }
    if (op === '>=') return { operator: '>=', value: num1 };
    if (op === '<=') return { operator: '<=', value: num1 };
    if (op === '>') return { operator: '>', value: num1 };
    if (op === '<') return { operator: '<', value: num1 };
    if (op === '=') return { operator: '=', value: num1 };
    return { operator: '=', value: num1 };
  }

  if (trimmed.startsWith('>=')) return { operator: '>=', value: trimmed.slice(2) };
  if (trimmed.startsWith('<=')) return { operator: '<=', value: trimmed.slice(2) };
  if (trimmed.startsWith('>')) return { operator: '>', value: trimmed.slice(1) };
  if (trimmed.startsWith('<')) return { operator: '<', value: trimmed.slice(1) };
  if (trimmed.startsWith('=')) return { operator: '=', value: trimmed.slice(1) };

  return { operator: '=', value: trimmed };
}

/**
 * Parses a query string into a structured state against a schema.
 */
export function parseQuery(query: string, schema?: QuerySchema<string>): ParsedQueryState {
  const trimmed = query.trim();
  if (!trimmed) {
    return { bareText: '', filters: [] };
  }

  const knownKeys = schema ? Object.keys(schema.fields) : undefined;
  const tokens = tokenize(trimmed, knownKeys);

  const bareWords: string[] = [];
  const filtersMap = new Map<string, ActiveFilter>();
  const filterList: ActiveFilter[] = [];

  let i = 0;
  while (i < tokens.length) {
    const token = tokens[i];

    if (token.type === TokenType.EOF) {
      break;
    }

    if (token.type === TokenType.Bare && token.value !== undefined) {
      bareWords.push(token.value);
      i++;
      continue;
    }

    // Check for parenthesized OR expression: (key:val1 OR key:val2)
    if (token.type === TokenType.LParen) {
      // Look ahead for (key:val OR key:val ...)
      let j = i + 1;
      const groupTokens: Token[] = [];
      while (j < tokens.length && tokens[j].type !== TokenType.RParen && tokens[j].type !== TokenType.EOF) {
        groupTokens.push(tokens[j]);
        j++;
      }

      const isOrGroup = groupTokens.some((t) => t.type === TokenType.OR);
      const groupFilters = groupTokens.filter((t) => t.type === TokenType.Filter && t.key && t.value !== undefined);

      if (isOrGroup && groupFilters.length > 1) {
        // Check if all filters share the same key
        const firstKey = groupFilters[0].key!;
        const sameKey = groupFilters.every((t) => t.key === firstKey);

        if (sameKey) {
          const values = groupFilters.map((t) => t.value!);
          const existing = filtersMap.get(firstKey);
          if (existing) {
            const set = new Set([...(existing.values || [existing.value]), ...values]);
            existing.values = Array.from(set);
            existing.value = existing.values.join(', ');
          } else {
            const filter: ActiveFilter = {
              id: generateFilterId(firstKey),
              key: firstKey,
              operator: '=',
              value: values.join(', '),
              values,
            };
            filtersMap.set(firstKey, filter);
            filterList.push(filter);
          }
          i = j + 1; // skip past RParen
          continue;
        }
      }
    }

    if (token.type === TokenType.Filter && token.key && token.value !== undefined) {
      const { key, value } = token;
      let { operator, value: parsedVal, highValue } = parseOperatorAndValue(value);

      // Normalize date filters
      if (key === 'from' || key === 'to') {
        operator = '=';
      }

      // Check if to: can be merged into an existing from: filter
      if (key === 'to') {
        const fromFilter = filtersMap.get('from');
        if (fromFilter) {
          fromFilter.highValue = parsedVal;
          i++;
          continue;
        }
      }

      // Check if schema field supports multiple values (enum) and operator is '='
      const fieldDef = schema?.fields[key];
      const isEnum = fieldDef?.type === 'enum';

      const existing = filtersMap.get(key);
      if (existing && isEnum && existing.operator === '=' && operator === '=') {
        const currentVals = existing.values || [existing.value];
        if (!currentVals.includes(parsedVal)) {
          currentVals.push(parsedVal);
          existing.values = currentVals;
          existing.value = currentVals.join(', ');
        }
      } else {
        const filter: ActiveFilter = {
          id: generateFilterId(key),
          key,
          operator,
          value: parsedVal,
          highValue,
        };
        filtersMap.set(key, filter);
        filterList.push(filter);
      }
      i++;
      continue;
    }

    // Skip standalone AND / OR tokens
    i++;
  }

  return {
    bareText: bareWords.join(' '),
    filters: filterList,
  };
}
